import Big from "big.js";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import BootstrapDropdown from "react-bootstrap/Dropdown";
import { AiOutlineCreditCard, AiOutlineFileExcel } from "react-icons/ai";
import {
  FiChevronDown,
  FiChevronRight,
  FiGrid,
  FiList,
  FiTag,
} from "react-icons/fi";
import { MdOutlineDiscount } from "react-icons/md";
import { RiCoupon2Line } from "react-icons/ri";
import { FormattedDate, FormattedMessage } from "react-intl";
import { connect, useSelector } from "react-redux";
import useSWRImmutable from "swr/immutable";
import { del, get, post } from "../../api";
import FormattedHelmet from "../../components/cmp/intl/FormattedHelmet";
import { ThreeDotsHorizontalIcon } from "../../icons/Icons";
import { actionMergeSetting } from "../../redux/settings/settingsAction";
import CardColumns from "../cmp/CardColumns";
import CurrencyNumber from "../cmp/CurrencyNumber";
import Button from "../cmp/form/Button";
import FormGroup from "../cmp/form/FormGroup";
import useToggle from "../cmp/hooks/useToggle";
import CardWithBody from "../cmp/layout/CardWithBody";
import LazyRangePicker from "../cmp/lazy/LazyRangePicker";
import LoadingSkeleton from "../cmp/LoadingSkeleton";
import Modal from "../cmp/Modal";
import Spinner from "../cmp/Spinner";
import StateBasedPagination from "../cmp/StateBasedPagination";
import StyledButton from "../cmp/themed/StyledButton";
import AffiliateSalesLineItemsModalNew from "./home/AffiliateSalesLineItemsModalNew";
import PaymentInvoiceUploader from "./payments/modals/PaymentInvoiceUploader";
import PaymentInputPage from "./payments/PaymentInputPage";
import RenderUserPayment from "./payments/RenderUserPaymentSettings";
import StoreDiscountCouponAutoRedeem from "./payments/StoreDiscountCouponAutoRedeem";
import { getComponent } from "./transactions/TransactionComponents";

function Payments({
  autopay_dc_enabled,
  can_request_payments,
  hide_pending_payment,
  payment_method,
  payment_details_data,
  aff_invoices_enabled,
}) {
  const {
    data: history,
    isLoading,
    mutate,
  } = useSWRImmutable("/payments/history", get);
  const {
    data: summary,
    isLoading: summaryLoading,
    mutate: mutateSummary,
  } = useSWRImmutable("/payments", get);
  const [activeFilter, setActiveFilter] = useState("");
  const [showHistoryAs, setShowHistoryAs] = useState("grid");
  const {
    amount_paid,
    amount_pending,
    next_payout_date,
    next_payout_amount,
    last_payment,
    amount_under_request,
    amount_payable,
    payout_lag_days,
  } = summary || {};
  const data = history?.history;
  const [lastUpdated, setLastUpdated] = useState(false);
  const subscription_plan = useSelector(
    (state) => state.settings.subscription_plan,
  );
  useEffect(() => {
    if (
      data &&
      !lastUpdated &&
      (subscription_plan === "business" || subscription_plan === "enterprise")
    ) {
      const filtered = data.filter(
        ({ payment_method }) => payment_method === "storegiftcard",
      );
      const withoutBalance = filtered.filter(({ payment_details }) => {
        const balance = payment_details?.balance;
        return balance === undefined || Number(balance) > 0;
      });
      if (withoutBalance.length > 0) {
        post("/payments/refresh_gift_card_balance").then((result) => {
          if (result.has_changed) {
            setLastUpdated(true);
            mutate();
          }
        });
      }
    }
  }, [data, lastUpdated, subscription_plan]);

  const refreshData = () => {
    mutate();
    mutateSummary();
  };
  const [hideUsedCodes, setHideUsedCodes] = useState(false);

  const filteredData = useMemo(() => {
    if (Array.isArray(data) && (hideUsedCodes || activeFilter)) {
      let d = data;
      if (activeFilter) {
        d = d.filter((i) =>
          activeFilter === "cash"
            ? i.payment_method !== "discountcoupon" &&
              i.payment_method !== "storegiftcard"
            : i.payment_method === "discountcoupon" ||
              i.payment_method === "storegiftcard",
        );
      }
      if (hideUsedCodes) {
        d = d.filter((i) => {
          const isCoupon =
            i.payment_method === "discountcoupon" ||
            i.payment_method === "storegiftcard";
          if (!isCoupon) return true;
          const isUsed =
            i.payment_details?.is_coupon_used ||
            i.payment_details?.balance == 0;
          return !isUsed;
        });
      }
      return d;
    }
    return data;
  }, [hideUsedCodes, activeFilter, data]);
  const usedPayouts = useMemo(() => {
    if (!data) return [];
    return data.filter((i) => {
      const isCoupon =
        i.payment_method === "discountcoupon" ||
        i.payment_method === "storegiftcard";
      if (!isCoupon) return true;
      return (
        i.payment_details?.is_coupon_used || i.payment_details?.balance == 0
      );
    });
  }, [data]);
  const subtitle = useSelector(
    (state) => state.settings.locale_messages["paymentsSubtitle"],
  );
  return (
    <>
      <FormattedHelmet id={"navlinkPayments"} />
      <div className={"mb-4"}>
        <div className={"row row-cols-1 row-cols-sm-2"}>
          <div className={"col"}>
            <h2>
              <FormattedMessage id={"navlinkPayments"} />
            </h2>
            <div className={"payment_subtitle"}>
              {String(subtitle).includes("<") ? (
                <div dangerouslySetInnerHTML={{ __html: subtitle }}></div>
              ) : (
                subtitle
              )}
            </div>
          </div>
          <div className={"col d-flex justify-content-end"}>
            <div className={"text-md-end"}>
              {payment_method ? (
                <div>
                  <RenderUserPayment
                    payment_method={payment_method}
                    payment_details_data={payment_details_data}
                  />
                </div>
              ) : null}
              <PaymentDetailsComponent />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between">
          <h4 className={"mt-4"}>
            <FormattedMessage id={"summary"} />
          </h4>
          <RenderMinimumPayout />
        </div>

        <div className={"card-group"}>
          <AmountPaid loading={summaryLoading} amount_paid={amount_paid} />
          {!hide_pending_payment && (
            <CardWithBody loading={summaryLoading} height={64}>
              <div className="d-flex align-items-start justify-content-between">
                <h3>
                  <CurrencyNumber value={amount_payable} />
                </h3>
                {payment_method === "discountcoupon" && autopay_dc_enabled && (
                  <StoreDiscountCouponCreator refreshPayments={refreshData} />
                )}
                {payment_method !== "discountcoupon" &&
                  can_request_payments &&
                  amount_payable > 0 &&
                  amount_under_request === 0 && (
                    <RequestPaymentButton
                      onRequestCompleted={refreshData}
                      amount_payable={amount_payable}
                    />
                  )}
              </div>
              <FormattedMessage tagName={"small"} id={"amountPending"} />
            </CardWithBody>
          )}
          {amount_under_request > 0 ? (
            <RequestPaymentCard
              amount_under_request={amount_under_request}
              onRequestCancelled={refreshData}
            />
          ) : null}
          {Number(payout_lag_days) &&
          Big(amount_pending || 0)
            .minus(amount_payable || 0)
            .gte(0) ? (
            <>
              <CardWithBody loading={summaryLoading} height={64}>
                <div className="d-flex align-items-start justify-content-between">
                  <h3>
                    <CurrencyNumber
                      value={Big(amount_pending)
                        .minus(amount_payable)
                        .minus(amount_under_request)
                        .toString()}
                    />
                  </h3>
                </div>
                <small>
                  <FormattedMessage
                    defaultMessage={"Due in {x, number} days"}
                    id={"Due in x days"}
                    values={{
                      x: payout_lag_days,
                      due: new Date(
                        Date.now() + Number(payout_lag_days) * 86400000,
                      ),
                    }}
                  />
                </small>
              </CardWithBody>
            </>
          ) : null}
          {next_payout_date ? (
            <CardWithBody loading={summaryLoading} height={64}>
              <div className="d-flex align-items-start justify-content-between">
                <h3>
                  <CurrencyNumber value={next_payout_amount} />
                </h3>
              </div>
              <small>
                Next expected payout on {dayjs(next_payout_date).format("MM/D")}
              </small>
            </CardWithBody>
          ) : null}
        </div>
        <div
          className={"mt-4 d-flex align-items-center justify-content-between"}
        >
          <h4 className={"d-inline-flex align-items-center"}>
            <FormattedMessage id={"details"} />
            <DownloadReportButton></DownloadReportButton>
          </h4>

          <div>
            {
              //has cash and credit
              Array.isArray(data) &&
              data.some(
                (i) =>
                  i.payment_method === "discountcoupon" ||
                  i.payment_method === "storegiftcard",
              ) &&
              data.some(
                (i) =>
                  i.payment_method !== "discountcoupon" &&
                  i.payment_method !== "storegiftcard",
              ) ? (
                <div className="btn-group mb-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveFilter((a) => (a === "cash" ? "" : "cash"))
                    }
                    title={"Show cash payouts"}
                    className={`btn btn-sm btn-outline-dark ${activeFilter === "cash" ? "active" : ""}`}
                  >
                    <AiOutlineCreditCard />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveFilter((a) =>
                        a === "discountcoupon" ? "" : "discountcoupon",
                      )
                    }
                    className={`btn btn-sm btn-outline-dark ${activeFilter === "discountcoupon" ? "active" : ""}`}
                    title={"Show store credit payouts"}
                  >
                    <RiCoupon2Line />
                  </button>
                </div>
              ) : null
            }

            <div className="btn-group ml-2 mb-2">
              {usedPayouts.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setHideUsedCodes((x) => !x)}
                  title={"View used codes"}
                  className={`btn btn-sm btn-outline-dark`}
                >
                  <FiTag />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setShowHistoryAs("list")}
                title={"Show as list"}
                className={`btn btn-sm btn-outline-dark ${
                  showHistoryAs === "list" ? "active" : ""
                }`}
              >
                <FiList />
              </button>
              <button
                type="button"
                onClick={() => setShowHistoryAs("grid")}
                className={`btn btn-sm btn-outline-dark ${showHistoryAs === "grid" ? "active" : ""}`}
                title={"Show as grid"}
              >
                <FiGrid />
              </button>
            </div>
          </div>
        </div>
        {showHistoryAs === "grid" ? (
          <>
            {isLoading ? (
              <>
                <div className="row">
                  {[...Array(3)].map((item, index) => (
                    <div key={String(index)} className="col-4">
                      <LoadingSkeleton height={64} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {Array.isArray(filteredData) && filteredData.length > 0 ? (
                  <CardColumns>
                    {filteredData.map((item) => (
                      <PayoutSectionItem
                        payout_id={item.payout_id}
                        aff_invoices_enabled={aff_invoices_enabled}
                        key={String(item.payout_id)}
                        {...item}
                      />
                    ))}
                  </CardColumns>
                ) : (
                  <p className={"alert alert-info"}>
                    <FormattedMessage id={"noPayments"} />
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {Array.isArray(filteredData) && filteredData.length > 0 ? (
              <>
                <div className={"d-flex flex-column gap-2"}>
                  {filteredData.map((item) => (
                    <PayoutSectionItem
                      payout_id={item.payout_id}
                      show_as={"list"}
                      aff_invoices_enabled={aff_invoices_enabled}
                      key={String(item.payout_id)}
                      {...item}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className={"alert alert-info"}>
                <FormattedMessage id={"noPayments"} />
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}

function DownloadReportButton() {
  const earnings_report_enabled = useSelector(
    (state) => state.settings.earnings_report_enabled,
  );
  const previousYear = dayjs().month() === 1;
  const [visible, toggle] = useToggle();
  const [dates, setDates] = useState([
    previousYear
      ? dayjs().subtract(1, "year").startOf("year").toDate()
      : dayjs().startOf("year").toDate(),
    previousYear
      ? dayjs().subtract(1, "year").endOf("year").toDate()
      : new Date(),
  ]);
  const [csvDownloading, setCSVDownloading] = useState(false);
  const downloadCSV = () => {
    const start = dayjs(dates[0]).format("YYYY-MM-DD");
    const end = dayjs(dates[1]).format("YYYY-MM-DD");
    setCSVDownloading(true);
    get(
      `/payments/history?start_date=${start}&end_date=${end}&download=csv`,
    ).then((res) => {
      setCSVDownloading(false);
      if (res.url) {
        window.location.href = res.url;
      }
    });
  };
  const [pdfDownloading, setPDFDownloading] = useState(false);
  const downloadPDF = () => {
    const start = dayjs(dates[0]).format("YYYY-MM-DD");
    const end = dayjs(dates[1]).format("YYYY-MM-DD");
    setPDFDownloading(true);
    get(
      `/payments/history?start_date=${start}&end_date=${end}&download=pdf`,
    ).then((res) => {
      setPDFDownloading(false);
      if (res.url) {
        window.location.href = res.url;
      }
    });
  };
  return (
    <>
      <button onClick={toggle} className={"btn"}>
        <AiOutlineFileExcel size={18} className={"text-muted"} />
      </button>
      <Modal
        visible={visible}
        onClose={toggle}
        title={<FormattedMessage id={"Download report"} />}
      >
        <FormGroup label={"Select date range"}>
          <LazyRangePicker
            onChange={setDates}
            startDate={dates[0]}
            endDate={dates[1]}
          ></LazyRangePicker>
        </FormGroup>
        <ul className="list-group">
          <li
            onClick={downloadCSV}
            style={{ cursor: "pointer" }}
            className="list-group-item list-group-item-action text-center py-4"
          >
            {csvDownloading ? (
              <Spinner></Spinner>
            ) : (
              <span>
                <FormattedMessage
                  id={"Download as CSV"}
                  defaultMessage={"Download as CSV"}
                ></FormattedMessage>
              </span>
            )}
          </li>

          {earnings_report_enabled ? (
            <li
              onClick={downloadPDF}
              style={{ cursor: "pointer" }}
              className="list-group-item  list-group-item-action text-center py-4 "
            >
              {pdfDownloading ? (
                <Spinner></Spinner>
              ) : (
                <span>
                  <FormattedMessage
                    id={"Download as PDF"}
                    defaultMessage={"Download as PDF"}
                  ></FormattedMessage>
                </span>
              )}
            </li>
          ) : null}
        </ul>
      </Modal>
    </>
  );
}
function PaymentDetailsModal({
  payout_id,
  metadata,
  amount,
  visible,
  onClose,
}) {
  const tx_ids = useMemo(
    () => (metadata ? JSON.parse(metadata)?.tx_ids : []),
    [metadata],
  );
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (visible && tx_ids.length > 0) {
      setLoading(true);
      get("/transactions", { payout_id }).then((data) => {
        setTxs(data.transactions);
        setLoading(false);
      });
    }
  }, [payout_id, tx_ids, visible]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  if (tx_ids.length === 0) {
    return null;
  }
  // group in sales vs other
  const sales = txs.filter(({ entity_type }) => entity_type === "orders");
  const other = txs.filter(({ entity_type }) => entity_type === "rewards");
  const level_commission = other.filter(
    ({ metadata }) => metadata.reward_type === "sale_commission",
  );
  const bonuses = other.filter(
    ({ metadata }) =>
      metadata.reward_type !== "sale_commission" &&
      metadata.reward_type !== "wallet_adjustment",
  );
  const wallet_adjustment = other.filter(
    ({ metadata }) => metadata.reward_type === "wallet_adjustment",
  );

  return (
    <>
      <AffiliateSalesLineItemsModalNew
        order_id={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
      <Modal
        visible={visible}
        title={<CurrencyNumber value={amount} />}
        onClose={onClose}
      >
        {loading ? (
          <LoadingSkeleton height={36.5} count={3} />
        ) : (
          <>
            {sales.length > 0 ? (
              <RenderPaginatedList
                loading={loading}
                title={"Sales"}
                list={sales}
              />
            ) : null}
            {level_commission.length > 0 ? (
              <RenderPaginatedList
                loading={loading}
                title={"Network"}
                list={level_commission}
              />
            ) : null}
            {bonuses.length > 0 ? (
              <RenderPaginatedList
                loading={loading}
                title={"Bonuses"}
                list={bonuses}
              />
            ) : null}
            {wallet_adjustment.length > 0 ? (
              <RenderPaginatedList
                loading={loading}
                title={"Adjustments"}
                list={wallet_adjustment}
              />
            ) : null}
            <div className="text-end px-4 py-2">
              <strong>
                <CurrencyNumber
                  value={txs
                    .reduce((acc, cur) => acc.plus(cur.amount), Big(0))
                    .toNumber()}
                />
              </strong>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
function RenderComponentItem({
  event_type,
  entity_type,
  entity_id,
  setSelectedOrderId,
  amount,
  metadata,
}) {
  const Cmp = getComponent(event_type, entity_type);
  if (Cmp) {
    return (
      <li className={"list-group-item space-between"}>
        <span>
          <Cmp
            metadata={metadata}
            entity_id={entity_id}
            onShowEventLog={
              entity_type === "orders"
                ? () => setSelectedOrderId(entity_id)
                : null
            }
          />
        </span>
        <span className={amount >= 0 ? "text-success" : "text-danger"}>
          <CurrencyNumber value={amount} />
        </span>
      </li>
    );
  } else {
    console.log("not found");
  }
}

function RenderPaginatedList({ list, loading, title }) {
  const [page, setPage] = useState(0);
  const [visible, toggle] = useToggle(false);
  const total = useMemo(() => {
    return list.reduce((acc, cur) => acc.plus(cur.amount), Big(0)).toNumber();
  }, [list]);
  const slicedList = useMemo(() => {
    return list.slice(page * 10, page * 10 + 10);
  }, [page, list]);
  return (
    <>
      <ul className="list-group mb-3">
        <li
          onClick={toggle}
          className="list-group-item d-flex align-items-center justify-content-between"
        >
          <span>{title}</span>
          {loading ? (
            <Spinner />
          ) : (
            <span>
              <span className={"text-success"}>
                <CurrencyNumber value={total} />
              </span>
              {!visible ? <FiChevronRight /> : <FiChevronDown />}
            </span>
          )}
        </li>

        {visible ? (
          <>
            {slicedList.map((i, index) => {
              return (
                <RenderComponentItem
                  key={String(index)}
                  metadata={i.metadata}
                  entity_id={i.entity_id}
                  amount={i.amount}
                  event_type={i.event_type}
                  entity_type={i.entity_type}
                  setSelectedOrderId={null}
                />
              );
            })}
            <li className="list-group-item">
              <StateBasedPagination
                count={list.length}
                limit={10}
                page={page}
                goToPage={setPage}
              />
            </li>
          </>
        ) : null}
      </ul>
    </>
  );
}

function StoreDiscountCouponCreator({ refreshPayments }) {
  const [autopay_dc_modal, toggleautopay_dc_modal] = useToggle();
  const onClose = () => {
    toggleautopay_dc_modal();
    refreshPayments();
  };
  return (
    <>
      <Button
        title={"Redeem"}
        className="btn btn-sm btn-outline-primary"
        onClick={toggleautopay_dc_modal}
      />
      <StoreDiscountCouponAutoRedeem
        visible={autopay_dc_modal}
        onClose={onClose}
      />
    </>
  );
}
function RenderMinimumPayout() {
  const minimum_payout = useSelector((state) => state.settings.minimum_payout);
  if (!minimum_payout || !Number(minimum_payout)) return null;
  return (
    <div className={"text-muted"}>
      <FormattedMessage id={"minimumPayout"} /> {":"}{" "}
      <CurrencyNumber value={minimum_payout} />
    </div>
  );
}
function RequestPaymentCard({ amount_under_request, onRequestCancelled }) {
  const [cancellingPaymentRequest, setCancelPaymentRequest] = useState(false);
  const cancelPaymentRequest = () => {
    setCancelPaymentRequest(true);
    del("/payments/request").then((row) => {
      if (row.success) {
        setCancelPaymentRequest(false);
        onRequestCancelled();
      }
    });
  };
  return (
    <CardWithBody>
      <div className="d-flex align-items-start justify-content-between">
        <h3>
          <CurrencyNumber value={amount_under_request} />
        </h3>
        <Button
          title={"Cancel"}
          className="btn btn-sm btn-outline-danger"
          onClick={cancelPaymentRequest}
          loading={cancellingPaymentRequest}
        />
      </div>
      <div className="space-between">
        <FormattedMessage tagName={"small"} id={"Amount Requested"} />
      </div>
    </CardWithBody>
  );
}

function RequestPaymentV2Button({ onRequestCompleted }) {
  const [visible, toggle] = useToggle();

  return (
    <>
      <ModalRequestPaymentV2
        onRequestCompleted={onRequestCompleted}
        visible={visible}
        onClose={toggle}
      />
      <Button
        title={"Request payment"}
        className="btn btn-sm btn-outline-primary"
        onClick={toggle}
      />
    </>
  );
}
function ModalRequestPaymentV2({ visible, onRequestCompleted, onClose }) {
  const { data, isLoading } = useSWRImmutable(
    visible ? "/payments/unpaid_transactions" : null,
    get,
  );
  const [value, setValue] = useState(0);
  const setChanged = (e) => {
    setValue(e.target.value);
  };
  const [note, setNote] = useState("");
  const [file, onFileUpload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const amount = Math.max(
    0,
    data?.unpaid_transactions
      .slice(0, value)
      .reduce((acc, cur) => acc.plus(cur.amount), Big(0))
      .toNumber(),
  );
  const onSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSubmitting(true);
    const txs = data?.unpaid_transactions.slice(0, value);
    post("/payments/request", {
      amount: txs.reduce((acc, cur) => acc.plus(cur.amount), Big(0)).toNumber(),
      tx_ids: txs.map((i) => i.tx_id),
    }).then((result) => {
      if (result.error) {
        setResult(result);
      } else {
        onRequestCompleted();
      }
      setSubmitting(false);
    });
  };
  // to account for any negative transactions
  return (
    <Modal
      visible={visible}
      okText={"Submit"}
      onSubmit={onSubmit}
      confirmLoading={submitting}
      disabled={value === 0}
      onCancel={onClose}
      title={"Request payment"}
    >
      {isLoading ? (
        <div
          className="text-center"
          style={{ paddingTop: 50, paddingBottom: 50 }}
        >
          <Spinner />
        </div>
      ) : (
        <div className="px-4 py-2">
          <p className={"text-center"}>
            <FormattedMessage
              id={"payment_request_description"}
              defaultMessage={
                "Move the slider to select the amount you want to request"
              }
            />
          </p>
          <input
            className={"form-range"}
            style={{ "--bs-tertiary-bg": "#bdc3c7" }}
            type={"range"}
            min={0}
            max={data?.unpaid_transactions.length}
            value={value}
            onChange={setChanged}
          />
          <div className={"text-center display-4"}>
            <CurrencyNumber value={amount} />
          </div>

          {/*<FormGroup>*/}
          {/*    <Label title={"Invoice"}/>*/}
          {/*    <SimpleFileUpload onChange={onFileUpload}/>*/}
          {/*</FormGroup>*/}
          {/*<FormGroup>*/}
          {/*    <Label title={"Note"}/>*/}
          {/*    <textarea className="form-control"*/}
          {/*              value={note}*/}
          {/*              onChange={setNote}/>*/}
          {/*</FormGroup>*/}
        </div>
      )}
      {result?.error ? (
        <p className="alert alert-danger">
          <FormattedMessage
            id={result?.id || result?.error}
            values={result.values}
            defaultMessage={result?.error}
          />
        </p>
      ) : null}
    </Modal>
  );
}

function RequestPaymentButton({ amount_payable, onRequestCompleted }) {
  const can_request_payments = useSelector(
    (state) => state.settings.can_request_payments,
  );
  if (can_request_payments === "payouts_v2") {
    return (
      <RequestPaymentV2Button
        onRequestCompleted={onRequestCompleted}
        amount_payable={amount_payable}
      />
    );
  } else {
    return (
      <RequestPaymentV1Button
        onRequestCompleted={onRequestCompleted}
        amount_payable={amount_payable}
      />
    );
  }
}

function RequestPaymentV1Button({ amount_payable, onRequestCompleted }) {
  const [invoice_upload_modal, toggleInvoiceUploadModal] = useToggle(false);
  const onClose = (result) => {
    toggleInvoiceUploadModal();
    if (result) {
      onRequestCompleted();
    }
  };
  return (
    <>
      <PaymentInvoiceUploader
        min={0}
        max={amount_payable}
        visible={invoice_upload_modal}
        onClose={onClose}
      />
      <Button
        title={"Request payment"}
        className="btn btn-sm btn-outline-primary"
        onClick={toggleInvoiceUploadModal}
      />
    </>
  );
}

function LastPayment({ last_payment, loading }) {
  return (
    <CardWithBody loading={loading} height={64}>
      <h3>
        {" "}
        {last_payment ? (
          <FormattedDate
            value={last_payment}
            day={"numeric"}
            month={"long"}
            year={"numeric"}
          />
        ) : (
          "-"
        )}
      </h3>
      <small>
        <FormattedMessage id={"lastPayment"} />
      </small>
    </CardWithBody>
  );
}
function AmountPaid({ amount_paid, loading }) {
  return (
    <CardWithBody loading={loading} height={64}>
      <h3>
        <CurrencyNumber value={amount_paid} />
      </h3>
      <small>
        <FormattedMessage id={"amountPaid"} />
      </small>
    </CardWithBody>
  );
}

function PaymentDetailsComponent() {
  const aff_settings_payment = useSelector(
    (state) => state.settings.aff_settings_payment,
  );
  const payment_method = useSelector((state) => state.settings.payment_method);
  const [paymentsModal, togglePaymentsModal] = useToggle();
  if (!aff_settings_payment) return null;
  return (
    <>
      <PaymentInputPage visible={paymentsModal} onClose={togglePaymentsModal} />
      {payment_method ? (
        <>
          <button
            className={"btn btn-link btn-sm px-0"}
            onClick={togglePaymentsModal}
          >
            <FormattedMessage id={"Change"} />
          </button>
        </>
      ) : (
        <StyledButton
          className={"btn btn-primary btn-sm"}
          onClick={togglePaymentsModal}
        >
          <FormattedMessage id={"Setup"} />
        </StyledButton>
      )}
    </>
  );
}

function PayoutSectionItem({
  aff_invoices_enabled,
  downloadToken,
  show_as,
  amount,
  payout_date,
  partner_message,
  payment_method,
  metadata,
  payout_id,
  payment_details,
}) {
  const [visible, toggleVisible] = useToggle();
  const aff_show_order_details = useSelector(
    (state) => state.settings.aff_show_order_details,
  );
  return (
    <>
      {aff_show_order_details && metadata ? (
        <PaymentDetailsModal
          visible={visible}
          amount={amount}
          payout_id={payout_id}
          onClose={toggleVisible}
          metadata={metadata}
        />
      ) : null}
      <div className={"card"} style={{ width: "100%" }}>
        <div className={"card-body"}>
          <div className={"row justify-content-between"}>
            <div className={"col-lg-6"}>
              <h5
                onClick={toggleVisible}
                className="animated-border"
                style={{ cursor: "pointer" }}
                title={"View payment details"}
              >
                <RenderPaymentTitle
                  payment_method={payment_method}
                  amount={amount}
                  conversion_rate={payment_details?.conversion_rate}
                  payout_currency={payment_details?.payout_currency}
                  payment_details_data={payment_details}
                />
              </h5>
            </div>
            <div className={"col-lg-6"}>
              {aff_invoices_enabled && (
                <div className="text-right">
                  <BootstrapDropdown>
                    <BootstrapDropdown.Toggle
                      as={ThreeDotsHorizontalIcon}
                      className={"text-muted p-0"}
                    ></BootstrapDropdown.Toggle>
                    <BootstrapDropdown.Menu>
                      <BootstrapDropdown.Item
                        href={`https://api-server-3.goaffpro.com/partner/payments/download/${downloadToken}`}
                      >
                        <FormattedMessage id={"Download invoice"} />
                      </BootstrapDropdown.Item>
                    </BootstrapDropdown.Menu>
                  </BootstrapDropdown>
                </div>
              )}
              <small className={"text-muted d-block text-right"}>
                {
                  <FormattedDate
                    value={new Date(payout_date)}
                    month={"short"}
                    year="numeric"
                    day={"2-digit"}
                  />
                }
              </small>
            </div>
          </div>
          <RenderUserPayment
            payment_method={payment_method}
            payment_details_data={payment_details}
          />
        </div>
        {partner_message && (
          <p
            className={"bg-light m-2 p-2 rounded"}
            style={{ whiteSpace: "pre-line" }}
          >
            {partner_message}
          </p>
        )}
      </div>
    </>
  );
}
function RenderPaymentTitle({
  amount,
  payout_currency,
  conversion_rate,
  payment_method,
  payment_details_data,
}) {
  const default_currency = useSelector(
    (state) => state.settings.default_currency,
  );
  const actual_amount = useMemo(() => {
    return conversion_rate && payout_currency
      ? Big(amount || 0)
          .times(conversion_rate)
          .toNumber()
      : amount;
  }, [amount, conversion_rate]);
  const coupon_value = useMemo(() => {
    if (payment_details_data) {
      const obj =
        typeof payment_details_data === "string"
          ? JSON.parse(payment_details_data)
          : payment_details_data;

      if (obj?.coupon_data) {
        const discount_value =
          obj?.discount_value ||
          obj?.discount_amount ||
          obj.coupon_data.discount_value;
        const discount_type =
          obj?.discount_type || obj.coupon_data.discount_type;
        return {
          obj,
          discount_value,
          discount_type,
          data: obj.coupon_data,
        };
      }
    }
  }, [payment_details_data]);
  // return <pre>{JSON.stringify(coupon_value, null, 2)}</pre>
  if (coupon_value) {
    if (coupon_value.discount_type === "percentage") {
      return <span>{coupon_value.discount_value}% off</span>;
    } else if (Number(amount) > 0 || Number(coupon_value.discount_value) > 0) {
      return (
        <span>
          <CurrencyNumber
            value={Number(amount) || Number(coupon_value.discount_value)}
          />
        </span>
      );
    } else if (payment_method === "discountcoupon") {
      return (
        <span>
          <MdOutlineDiscount />
        </span>
      );
    } else return <span>---</span>;
  } else {
    if (Number(amount) > 0) {
      return (
        <div>
          <CurrencyNumber
            currency_display={
              payout_currency !== default_currency ? "symbol" : undefined
            }
            currency={payout_currency}
            value={actual_amount}
          />

          {payout_currency && payout_currency !== default_currency && (
            <span
              style={{ fontSize: "0.75rem" }}
              className={"text-muted mt-2 d-block"}
            >
              ({<CurrencyNumber value={amount} currency={default_currency} />})
            </span>
          )}
        </div>
      );
    } else if (payment_method === "discountcoupon") {
      return (
        <span>
          <MdOutlineDiscount />
        </span>
      );
    } else return <span>---</span>;
  }
}

function mapStateToProps(state) {
  const {
    active_payment_request_id,
    affiliate_portal_config,
    can_request_payments,
  } = state.settings;
  const hide_pending_payment = affiliate_portal_config
    ? JSON.parse(affiliate_portal_config).hide_pending_payment
    : false;
  return {
    minimum_payout: state.settings.minimum_payout,
    payment_method: state.settings.payment_method,
    payment_details_data: state.settings.payment_details_data,
    autopay_dc_enabled: state.settings.autopay_dc_enabled,
    w9_enabled: state.settings.w9_enabled,
    aff_settings_payment:
      state.settings.aff_settings_payment === undefined
        ? true
        : state.settings.aff_settings_payment,
    aff_invoices_enabled: state.settings.aff_invoices_enabled,
    can_request_payments,
    active_payment_request_id,
    hide_pending_payment,
    payment_request_need_invoice:
      true || state.settings.payment_request_need_invoice,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    mergeSettings: (change) => dispatch(actionMergeSetting(change)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Payments);
